import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import PubSub from 'pubsub-js'
import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { CombinedGraphQLErrors } from '@apollo/client'
import Spinner from '../Spinner/Spinner'
import AppObjectActionForm from '../AppObjectActions/AppObjectActionForm'
import AppObjectActionButtons from '../AppObjectActions/AppObjectActionButtons'
import AppObjectViewButton from '../AppObjectViewButton/AppObjectViewButton'
import AppObjectError from '../AppObjectError/AppObjectError'
import AppObjectBody from '../AppObjectBody/AppObjectBody'
import Upload from '../Upload/Upload'
import { EXECUTE_ACTION_MUTATION, CANCEL_ACTION_MUTATION, SET_USER_ATTRIBUTE_MUTATION } from '../../graphql'
import { ACTION_TYPE, OBJECT_VIEW_STYLE, ATTRIBUTE_NAME, FIELD_TYPE } from '../../constants'
import { validateInput } from '../../validate'
import {
  createInitialFormInput, createFieldLookup, publishEvent, getTenant, uploadFile,
  makeInteger, getAttributeValue, setAttributeValue
} from '../../utils'
import './AppObject.css'
import AppObjectPrintViewButton from '../AppObjectPrintViewButton/AppObjectPrintViewButton'
import PrintButton from '../PrintButton/PrintButton'

const EXECUTE_ACTION = gql(EXECUTE_ACTION_MUTATION)
const CANCEL_ACTION = gql(CANCEL_ACTION_MUTATION)
const SET_USER_ATTRIBUTE = gql(SET_USER_ATTRIBUTE_MUTATION)

export default function AppObject(props) {
  const { object, action, objectType, parentObjectId, refListId, existingFormInput, initialSource } = props
  console.log(`------------------------------ AppObject rendered (${object.type.name}: ${object.id}) ------------------------------`)
  console.log(props)

  function initialChangeCount() {
    let value = 0
    if (action && (makeInteger(action.type.id) === ACTION_TYPE.changeState || makeInteger(action.type.id) === ACTION_TYPE.submit || object.submitting)) {
      // CHANGE_STATE and SUBMIT actions must be allowed to save without changes
      value = 1
    }
    if (existingFormInput) {
      for (let field in existingFormInput) {
        if (existingFormInput[field] && existingFormInput[field].changed) {
          value += 1
        }
      }
    }
    return value
  }

  function initialFormInput() {
    console.log('AppObject.initialFormInput')
    let value = null
    if (existingFormInput) {
      value = existingFormInput
    } else {
      value = createInitialFormInput(object, action)
      console.log(value)
      value.files = {}
    }
    // save a reference to the root of the form input
    if (!refListId) {
      window.rootFormInputs = value
    }
    return value
  }

  const [source] = useState(initialSource)
  const [changeCount, setChangeCount] = useState(initialChangeCount)
  const [formInputs] = useState(initialFormInput) //possible optimization later with refs if needed
  //formInputs.action = action
  const [errors, setErrors] = useState('')
  const tabStyle = getAttributeValue(ATTRIBUTE_NAME.tabStyle)
  const [viewStyle, setViewStyle] = useState(tabStyle ? tabStyle : OBJECT_VIEW_STYLE.tab)
  const navigate = useNavigate()
  const location = useLocation()
  const printView = location.pathname.includes('printView') ? true : false
  const [executeAction, { loading: mutationLoading }] = useMutation(EXECUTE_ACTION)
  const [cancelAction] = useMutation(CANCEL_ACTION)
  const [setUserAttribute] = useMutation(SET_USER_ATTRIBUTE)

  useEffect(() => {
    function handleSetErrorsEvent(msg, data) {
      console.log(`EVENT RECEIVED (AppObject of type ${object.type.name}): ${msg}`, data)
      setAppObjectErrors(data, 'AppObject.useEffect.handleSetErrorsEvent')
    }
    function handleToggleViewStyleEvent(msg, data) {
      console.log(`EVENT RECEIVED (AppObject of type ${object.type.name}): ${msg}`, data)
      toggleViewStyle()
    }
    function handleOkButtonEvent(msg, data) {
      console.log(`EVENT RECEIVED (AppObject of type ${object.type.name}): ${msg}`, data)
      okButtonPressed()
    }
    function handleCancelChangesEvent(msg, data) {
      console.log(`EVENT RECEIVED (AppObject of type ${object.type.name}): ${msg}`, data)
      cancelChanges()
    }
    function handleDeleteObjectEvent(msg, data) {
      console.log(`EVENT RECEIVED (AppObject of type ${object.type.name}): ${msg}`, data)
      formInputs.action = data
      okButtonPressed()
    }
    function handleChangeCountEvent(msg, data) {
      console.log(`EVENT RECEIVED (AppObject of type ${object.type.name}): ${msg}`, data)
      // Notify parent object
      // if (((changeCount === 0 && data === 1) || (changeCount === 1 && data === -1)) && parentObjectId) {
      //   publishEvent(`CHANGE COUNT ${parentObjectId}`, data, 'AppObject')
      // }
      setChangeCount(changeCount + data)
    }
    function handleFileSelectEvent(msg, data) {
      console.log(`EVENT RECEIVED (AppObject ${object.type.name}): ${msg}`, data)
      if (!parentObjectId) {
        const index = `${data.objectId}-${data.fieldId}`
        if (!data.file) {
          delete formInputs.files[index]
        } else {
          formInputs.files[index] = {
            objectId: data.objectId,
            fieldId: data.fieldId,
            file: data.file,
            formInputs: data.formInputs,
          }
          console.log('--------------------------------------- AppObject.handleFileSectedEvent', formInputs.files)
        }
        publishEvent(`UPLOAD CHANGE ${object.id}`, data, 'AppObject')
      } else {
        publishEvent(`FILE SELECTED ${parentObjectId}`, data, 'AppObject')
      }
    }
    const setErrorsToken = PubSub.subscribe(`SET ERRORS ${object.id}`, handleSetErrorsEvent)
    const toggleViewStyleToken = PubSub.subscribe(`TOGGLE VIEW STYLE ${object.id}`, handleToggleViewStyleEvent)
    const okButtonToken = PubSub.subscribe(`OK BUTTON ${object.id}`, handleOkButtonEvent)
    const cancelChangesToken = PubSub.subscribe(`CANCEL CHANGES ${object.id}`, handleCancelChangesEvent)
    const deleteObjectToken = PubSub.subscribe(`DELETE OBJECT ${object.id}`, handleDeleteObjectEvent)
    const changeCountToken = PubSub.subscribe(`CHANGE COUNT ${object.id}`, handleChangeCountEvent)
    const fileSelectToken = PubSub.subscribe(`FILE SELECTED ${object.id}`, handleFileSelectEvent)
    return function cleanup() {
      PubSub.unsubscribe(setErrorsToken)
      PubSub.unsubscribe(toggleViewStyleToken)
      PubSub.unsubscribe(okButtonToken)
      PubSub.unsubscribe(cancelChangesToken)
      PubSub.unsubscribe(deleteObjectToken)
      PubSub.unsubscribe(changeCountToken)
      PubSub.unsubscribe(fileSelectToken)
    }
  })

  function setAppObjectErrors(appObjectErrors, context) {
    setErrors(appObjectErrors)
    publishEvent(`ENABLE OK BUTTON ${object.id}`, null, context)
  }

  function toggleViewStyle() {
    const newViewStyle = viewStyle === OBJECT_VIEW_STYLE.card ? OBJECT_VIEW_STYLE.tab : OBJECT_VIEW_STYLE.card
    setUserAttribute({ variables: { attributeName: ATTRIBUTE_NAME.tabStyle, attributeValue: newViewStyle } })
    setAttributeValue(ATTRIBUTE_NAME.tabStyle, newViewStyle)
    setViewStyle(newViewStyle)
  }

  function buildChangesObject(object, newFormInputs) {
    console.log('AppObject.buildChangesObject: ', object, newFormInputs)
    const fieldsArray = createFieldLookup(object.type.fields)

    // create the input object for the mutation
    const changes = {
      object: {
        objectId: object.id,
        objectTypeName: object.type.name,
        tableName: object.type.tableName,
        pkColumnName: object.type.pkColumnName,
        fieldValues: [],
      }
    }

    if (newFormInputs.action) {
      changes.action = {
        actionId: newFormInputs.action.id,
        actionName: newFormInputs.action.name,
        actionTypeId: newFormInputs.action.type.id,
        actionTypeName: newFormInputs.action.type.name,
        destinationStateId: newFormInputs.action.destinationState ? newFormInputs.action.destinationState.id : null,
        destinationStateName: newFormInputs.action.destinationState ? newFormInputs.action.destinationState.name : null,
        finalizationHook: newFormInputs.action.finalizationHook
      }
    }

    // add changes for each of the fields that have changed
    for (let fieldId in newFormInputs) {
      const fieldInput = newFormInputs[fieldId]
      console.log(`-------------- newFormInputs[${fieldId}]`, typeof fieldId, parseInt(fieldId), fieldInput)
      if (fieldInput.changed) {
        console.log(`${fieldsArray[fieldId].name} changed: `, fieldInput)
        if (makeInteger(fieldInput.field.type.id) === FIELD_TYPE.referenceList) {
          // reference list field
          for (let refListObject of fieldInput.currentValue.objects) {
            if (fieldInput.currentValue[refListObject.id].action) {
              // recursive call to build change lists for reference list objects if it has changed
              const refListChanges = buildChangesObject(refListObject, fieldInput.currentValue[refListObject.id])
              changes.object.fieldValues.push(refListChanges)
            }
          }
        } else {
          // non-reference list field
          changes.object.fieldValues.push({
            fieldName: fieldsArray[fieldId].name,
            fieldId: fieldId,
            columnName: fieldsArray[fieldId].columnName,
            multiTableName: fieldsArray[fieldId].multiTableName,
            choiceTableName: fieldsArray[fieldId].choiceTableName,
            fieldTypeId: fieldsArray[fieldId].fieldTypeId,
            dataStoreHook: fieldsArray[fieldId].dataStoreHook,
            encrypt: fieldsArray[fieldId].encrypt,
            change: { value: fieldInput.currentValue }
          })
        }
      }
      // create the new form inputs array with value of 'savedValue' for use after save is successful
      //     fieldInput.savedValue = fieldInput.currentValue
      //     fieldInput.changed = false
    }
    return changes
  }

  function returnToSource() {
    if (source) {
      navigate(source)
    } else {
      navigate('/home')
    }
  }

  async function callExecuteAction(input) {
    // call the mutation
    await executeAction({ variables: { input: input } })
      .then((res) => {
        console.log('AppObject.callExecuteAction', res)
        returnToSource()
      })
      .catch((error) => {
        if (CombinedGraphQLErrors.is(error)) { 
          const errors = error.errors.map((error) => {
            return error.message
          })
          setAppObjectErrors(errors[0], 'AppObject.callExecuteAction')
        }
      })
  }

  async function uploadFiles() {
    console.log('AppObject: uploadFiles')

    function fileUploadsCompleted() {
      for (let fieldId in formInputs.files) {
        if (!formInputs.files[fieldId].uploaded) {
          return false
        }
      }
      return true
    }

    function uploadCallback(e, fileInfo) {
      console.log('------------------------------------------------ uploadCallback: ', e, fileInfo)

      if (e.type === 'load' && e.target.status === 201) {
        //const newFileName = JSON.parse(e.target.response).newFileName
        //fileInfo.formInputs[fileInfo.fieldId].currentValue = newFileName
        fileInfo.uploaded = true
        fileInfo.setUploaded(true)
      }
      if ((e.type === 'load' && e.target.status !== 201) || e.type === 'error') {
        let errorText = e.target.responseText ? e.target.responseText : 'check browser console for error description.'
        fileInfo.setErrorText(errorText)
        setAppObjectErrors('File upload error.', 'AppObject.uploadCallback')
      }
      if (e.type === 'progress' && e.lengthComputable) {
        const percentage = Math.round(e.loaded / e.total * 100)
        fileInfo.setPercentage(percentage)
      }
      if (fileUploadsCompleted()) {
        saveChanges()
      }
    }

    // check if there are any files to be uploaded
    if (!fileUploadsCompleted()) {
      for (let fileId in formInputs.files) {
        const fieldId = formInputs.files[fileId].fieldId
        const newFileName = `FP${parseInt(Math.floor(Math.random() * 1000000000))}/${formInputs.files[fileId].file.name}`
        formInputs.files[fileId].newFileName = newFileName
        formInputs.files[fileId].formInputs[fieldId].currentValue = newFileName
        const tenant = getTenant()
        const url = `${process.env.REACT_APP_SERVER}/upload/${tenant}`
        uploadFile(url, formInputs.files[fileId], uploadCallback)
      }
    } else {
      saveChanges()
    }
  }

  async function saveChanges() {
    console.log('AppObject: saveChanges')
    delete formInputs.files

    // recursively build the list of changes to be saved
    const changes = buildChangesObject(object, formInputs)

    // call the executeAction mutation
    await callExecuteAction(changes)
  }

  function cancelChanges() {
    console.log('AppObject: cancelChanges')

    console.log(`AppObject: executing cancelAction(${object.id}, ${action.id}) mutation...`)
    cancelAction({ variables: { objectId:  object.id, actionId: action.id } })
    returnToSource()
  }

  async function okButtonPressed() {
    console.log('AppObject.okButtonPressed')
    const validationErrors = []
    await validateInput(formInputs, validationErrors, object)
    console.log('validationErrors: ', validationErrors)
    if (validationErrors.length === 0) {
      uploadFiles()
    } else {
      setAppObjectErrors(validationErrors, 'AppObject.okButtonPressed')
    }
  }

  function closeButtonClicked() {
    console.log(`AppObject.closeButtonClicked: ${object.id}`)
    publishEvent(`CLOSE BUTTON ${object.id}`, null, 'AppObject.closeButtonClicked')
  }

  let className = 'app-object'
  let containerClassName = 'app-object-scroll-container'
  let closeButton = ''
  let actionComponent = ''
  let printViewButton = ''
  if (parentObjectId) {
    className += ' app-object-child'
    containerClassName = 'app-object-child-container'
    if (action) {
      actionComponent = <AppObjectActionButtons object={object} refListId={refListId} action={action} />
    } else {
      closeButton = <button type="button" style={{ float: 'right' }} aria-label="Close"><span aria-hidden="true" onClick={closeButtonClicked}>×</span></button>
    }
  } else {
    if (action) {
      actionComponent = <AppObjectActionButtons object={object} refListId={refListId} action={action} />
    } else {
      actionComponent = <AppObjectActionForm object={object} source={source} />
      printViewButton = <AppObjectPrintViewButton objectId={object.id} />
    }
  }

  if (object.inUse && !object.inUse.displayed) {
    object.inUse.displayed = true
    setAppObjectErrors(`WARNING: This record was loaded for modification by ${object.inUse.userName} on ${object.inUse.actionDate}.`, 'AppObject.inUse')
  }

  if (printView) {
    const elementId = 'object-print-view'
    document.body.style.paddingTop = '0px'
    return (
      <>
        <PrintButton elementId={elementId} includeCloseButton={true} />
        <div id={elementId} style={{ height: 'calc(100% - 60px)', overflow: 'auto', marginLeft: '10px' }}>
          <AppObjectBody object={object} formInputs={formInputs} viewStyle={OBJECT_VIEW_STYLE.card} />
        </div>
      </>
    )
  }

  return (
    <form name="autocomplete" style={{ height: '100%' }}>
      <div style={{ height: '100%'}} data-cy="object" id={`${object.id}`} data-cy-object={object.type.name.replace(/ /g, '-')} >
        <div style={{ margin: '10px' }}>
          {actionComponent}
        </div>
        <AppObjectViewButton objectId={object.id} />{printViewButton}
        <div id="app-object-scroll-container" className={containerClassName}>
          <div className={className}>
            {mutationLoading && <Spinner />}
            <Upload objectId={object.id} files={formInputs.files} />
            <AppObjectError errors={errors} parentObjectId={parentObjectId} />
            {closeButton}
            <AppObjectBody object={object} parentObjectId={parentObjectId} action={action} objectType={objectType} formInputs={formInputs} viewStyle={viewStyle} />
          </div>
        </div>
      </div>
    </form>
  )
}
